import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, EnvelopeSimple, Lock, User, GoogleLogo, CheckCircle, XCircle, Eye, EyeSlash, ArrowRight } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { PassportLogo } from "@/components/Reveal";
import { authApi } from "@/lib/apiClient";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

const PASSWORD_RULES = [
    { id: "length",    test: (pw) => pw.length >= 8 },
    { id: "upper",     test: (pw) => /[A-Z]/.test(pw) },
    { id: "lower",     test: (pw) => /[a-z]/.test(pw) },
    { id: "number",    test: (pw) => /[0-9]/.test(pw) },
    { id: "special",   test: (pw) => /[!@#$%^&*()_+\-=\[\]{}|;':",./\<>?]/.test(pw) },
];

export default function Auth() {
    const { t } = useTranslation();
    const [sp] = useSearchParams();
    const [mode, setMode] = useState(sp.get("mode") === "signup" ? "signup" : "signin");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [name, setName] = useState("");
    const [loading, setLoading] = useState(false);
    const [pwFocused, setPwFocused] = useState(false);
    const [showPw, setShowPw] = useState(false);
    const [showConfirmPw, setShowConfirmPw] = useState(false);

    // Forgot password flow state
    const [forgotMode, setForgotMode] = useState(false); // "email" | "otp" | false
    const [forgotEmail, setForgotEmail] = useState("");
    const [forgotOtp, setForgotOtp] = useState("");
    const [forgotNewPw, setForgotNewPw] = useState("");
    const [showForgotPw, setShowForgotPw] = useState(false);
    const [forgotLoading, setForgotLoading] = useState(false);

    const { signin, signup } = useAuth();
    const nav = useNavigate();

    const submit = async (e) => {
        e.preventDefault();
        if (mode === "signup") {
            const failing = PASSWORD_RULES.filter((r) => !r.test(password));
            if (failing.length > 0) {
                toast.error(t("auth.rules.required", { rule: t(`auth.rules.${failing[0].id}`) }));
                return;
            }
            if (password !== confirmPassword) {
                toast.error(t("auth.toast.passwordsDoNotMatch"));
                return;
            }
        }
        setLoading(true);
        try {
            if (mode === "signin") {
                await signin(email, password);
                toast.success(t("auth.toast.welcomeBack"));
                nav("/app");
            } else {
                const res = await signup(name, email, password);
                if (res?.status === "pending_verification") {
                    toast.success(t("auth.toast.accountCreatedVerification"));
                    nav(`/verify-email?email=${encodeURIComponent(res.email)}`);
                } else {
                    toast.success(t("auth.toast.welcome"));
                    nav("/app");
                }
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || t("auth.toast.authFailed"));
        } finally {
            setLoading(false);
        }
    };

    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const googleLogin = () => {
        const redirectUrl = window.location.origin + "/auth/callback";
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    // --- Forgot password handlers ---
    const handleForgotSubmitEmail = async (e) => {
        e.preventDefault();
        setForgotLoading(true);
        try {
            await authApi.forgotPassword(forgotEmail);
            toast.success(t("auth.toast.resetSent"));
            setForgotMode("otp");
        } catch (err) {
            toast.error(err?.response?.data?.detail || t("auth.toast.resetSendError"));
        } finally {
            setForgotLoading(false);
        }
    };

    const handleForgotSubmitOtp = async (e) => {
        e.preventDefault();
        const failing = PASSWORD_RULES.filter((r) => !r.test(forgotNewPw));
        if (failing.length > 0) {
            toast.error(t("auth.rules.required", { rule: t(`auth.rules.${failing[0].id}`) }));
            return;
        }
        setForgotLoading(true);
        try {
            await authApi.resetPassword(forgotEmail, forgotOtp, forgotNewPw);
            toast.success(t("auth.toast.passwordResetSuccess"));
            setForgotMode(false);
            setForgotEmail(""); setForgotOtp(""); setForgotNewPw("");
            setMode("signin");
        } catch (err) {
            toast.error(err?.response?.data?.detail || t("auth.toast.passwordResetError"));
        } finally {
            setForgotLoading(false);
        }
    };

    const allPassed = PASSWORD_RULES.every((r) => r.test(password));
    const showStrength = mode === "signup" && (pwFocused || password.length > 0);

    // ----- Forgot Password Modal -----
    if (forgotMode) {
        return (
            <div className="min-h-screen relative grain flex flex-col" data-testid="forgot-password-page">
                <div className="p-6 md:p-10">
                    <button
                        onClick={() => { setForgotMode(false); setForgotEmail(""); setForgotOtp(""); setForgotNewPw(""); }}
                        className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-[color:var(--gold)]"
                        data-testid="forgot-back"
                    >
                        <ArrowLeft size={14} /> {t("auth.back")}
                    </button>
                </div>

                <div className="flex-1 flex items-center justify-center px-6 pb-16">
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="w-full max-w-md">
                        <div className="text-center mb-10">
                            <div className="w-16 h-16 mx-auto mb-6 rounded-2xl grid place-items-center" style={{ background: "rgba(212,175,55,0.1)", border: "1px solid rgba(212,175,55,0.3)" }}>
                                <PassportLogo size={40} />
                            </div>
                            <h1 className="font-serif text-4xl text-white tracking-tighter">
                                {forgotMode === "email" ? t("auth.forgot.titleForgot") : t("auth.forgot.titleReset")}
                            </h1>
                            <div className="overline mt-3">
                                {forgotMode === "email" ? t("auth.forgot.descEmail") : t("auth.forgot.descOtp", { email: forgotEmail })}
                            </div>
                        </div>

                        <div className="rounded-3xl p-8" style={{ background: "var(--navy-deep)", border: "1px solid rgba(255,255,255,0.06)" }}>
                            <AnimatePresence mode="wait">
                                {forgotMode === "email" && (
                                    <motion.form key="forgot-email" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onSubmit={handleForgotSubmitEmail} className="space-y-4">
                                        <Field label={t("auth.emailAddress")} icon={EnvelopeSimple}>
                                            <input
                                                required type="email" value={forgotEmail}
                                                onChange={(e) => setForgotEmail(e.target.value)}
                                                placeholder={t("auth.emailPlaceholder")} className="input"
                                                data-testid="forgot-email-input"
                                            />
                                        </Field>
                                        <button type="submit" disabled={forgotLoading} className="btn-gold w-full justify-center mt-6" data-testid="forgot-email-submit">
                                            {forgotLoading ? t("auth.forgot.sending") : t("auth.forgot.sendCode")}
                                        </button>
                                    </motion.form>
                                )}

                                {forgotMode === "otp" && (
                                    <motion.form key="forgot-otp" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onSubmit={handleForgotSubmitOtp} className="space-y-4">
                                        <Field label={t("auth.forgot.otpLabel")} icon={Lock}>
                                            <input
                                                required value={forgotOtp} onChange={(e) => setForgotOtp(e.target.value)}
                                                placeholder="000000" maxLength={6}
                                                className="input tracking-[0.4em] text-center font-serif text-xl"
                                                data-testid="forgot-otp-input"
                                            />
                                        </Field>
                                        <Field label={t("auth.forgot.newPasswordLabel")} icon={Lock}>
                                            <input
                                                required type={showForgotPw ? "text" : "password"}
                                                value={forgotNewPw} onChange={(e) => setForgotNewPw(e.target.value)}
                                                placeholder="••••••••" className="input !pr-12"
                                                data-testid="forgot-newpw-input"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowForgotPw((v) => !v)}
                                                className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/80 transition-colors"
                                                tabIndex={-1}
                                                data-testid="forgot-pw-eye"
                                            >
                                                {showForgotPw ? <EyeSlash size={16} /> : <Eye size={16} />}
                                            </button>
                                        </Field>
                                        {forgotNewPw.length > 0 && (
                                            <div className="rounded-xl p-3 space-y-1.5" style={{ background: "rgba(0,0,0,0.3)", border: "1px solid rgba(255,255,255,0.06)" }}>
                                                {PASSWORD_RULES.map((rule) => {
                                                    const passed = rule.test(forgotNewPw);
                                                    return (
                                                        <div key={rule.id} className="flex items-center gap-2 text-[11px]" style={{ color: passed ? "#4ade80" : "rgba(255,255,255,0.45)" }}>
                                                            {passed ? <CheckCircle size={12} weight="fill" color="#4ade80" /> : <XCircle size={12} weight="fill" color="rgba(255,255,255,0.25)" />}
                                                            {t(`auth.rules.${rule.id}`)}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                        <button type="submit" disabled={forgotLoading} className="btn-gold w-full justify-center mt-6" data-testid="forgot-otp-submit">
                                            {forgotLoading ? t("auth.forgot.resetting") : t("auth.forgot.resetSubmit")}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleForgotSubmitEmail}
                                            disabled={forgotLoading}
                                            className="w-full mt-2 text-xs text-white/50 hover:text-[color:var(--gold)] transition-colors"
                                            data-testid="forgot-resend"
                                        >
                                            {t("auth.forgot.resend")}
                                        </button>
                                    </motion.form>
                                )}
                            </AnimatePresence>
                        </div>
                    </motion.div>
                </div>
                <style>{`
                    .input { width: 100%; background: rgba(0,0,0,0.3); border: 1px solid rgba(255,255,255,0.08); border-radius: 12px; padding: 14px 16px 14px 44px; color: white; font-size: 14px; transition: border-color 0.3s ease; color-scheme: dark; }
                    .input::placeholder { color: rgba(255,255,255,0.35); }
                    .input:focus { outline: none; border-color: var(--gold); }
                    .input:-webkit-autofill, .input:-webkit-autofill:hover, .input:-webkit-autofill:focus, .input:-webkit-autofill:active { -webkit-box-shadow: 0 0 0 1000px #080e18 inset !important; -webkit-text-fill-color: #ffffff !important; caret-color: #ffffff; border-color: rgba(255,255,255,0.15) !important; }
                `}</style>
            </div>
        );
    }

    // ----- Main Auth Page -----
    return (
        <div className="min-h-screen relative grain flex flex-col" data-testid="auth-page">
            <div className="p-6 md:p-10">
                <Link to="/" className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-[color:var(--gold)]" data-testid="auth-back">
                    <ArrowLeft size={14} /> {t("auth.back")}
                </Link>
            </div>

            <div className="flex-1 flex items-center justify-center px-6 pb-16">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    className="w-full max-w-md"
                >
                    <div className="text-center mb-10">
                        <div className="w-16 h-16 mx-auto mb-6 rounded-2xl grid place-items-center" style={{ background: "rgba(212,175,55,0.1)", border: "1px solid rgba(212,175,55,0.3)" }}>
                            <PassportLogo size={40} />
                        </div>
                        <h1 className="font-serif text-5xl md:text-6xl text-white leading-none tracking-tighter">{t("conciergeApp.passport")}</h1>
                        <div className="overline mt-3">{t("auth.subtitle")}</div>
                    </div>

                    <div className="rounded-3xl p-8" style={{ background: "var(--navy-deep)", border: "1px solid rgba(255,255,255,0.06)" }}>
                        <div className="flex gap-2 mb-6 p-1 rounded-full" style={{ background: "rgba(0,0,0,0.3)" }}>
                            <button onClick={() => setMode("signin")}
                                className="flex-1 py-2.5 rounded-full text-sm transition-colors duration-300"
                                style={{ background: mode === "signin" ? "var(--gold)" : "transparent", color: mode === "signin" ? "var(--navy)" : "white" }}
                                data-testid="auth-tab-signin">
                                {t("auth.signin")}
                            </button>
                            <button onClick={() => setMode("signup")}
                                className="flex-1 py-2.5 rounded-full text-sm transition-colors duration-300"
                                style={{ background: mode === "signup" ? "var(--gold)" : "transparent", color: mode === "signup" ? "var(--navy)" : "white" }}
                                data-testid="auth-tab-signup">
                                {t("auth.signup")}
                            </button>
                        </div>

                        <button
                            onClick={googleLogin}
                            className="w-full flex items-center justify-center gap-3 py-3.5 rounded-full text-sm transition-colors duration-300 mb-5"
                            style={{ background: "white", color: "var(--navy)" }}
                            data-testid="auth-google"
                        >
                            <GoogleLogo size={18} weight="bold" />
                            {t("auth.continueWithGoogle")}
                        </button>

                        <div className="flex items-center gap-3 mb-5">
                            <div className="flex-1 h-px" style={{ background: "rgba(255,255,255,0.08)" }} />
                            <span className="text-[10px] tracking-[0.25em] uppercase text-white/40">{t("auth.orEmail")}</span>
                            <div className="flex-1 h-px" style={{ background: "rgba(255,255,255,0.08)" }} />
                        </div>

                        <form onSubmit={submit} className="space-y-4">
                            {mode === "signup" && (
                                <Field label={t("auth.name")} icon={User}>
                                    <input required value={name} onChange={(e) => setName(e.target.value)} placeholder="Amelia Reeve" className="input" data-testid="auth-name" />
                                </Field>
                            )}
                            <Field label={t("auth.email")} icon={EnvelopeSimple}>
                                <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@passport.com" className="input" data-testid="auth-email" />
                            </Field>

                            {/* Password field with eye toggle */}
                            <div>
                                <Field label={t("auth.password")} icon={Lock}>
                                    <input
                                        required
                                        type={showPw ? "text" : "password"}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        onFocus={() => setPwFocused(true)}
                                        onBlur={() => setPwFocused(false)}
                                        placeholder="••••••••"
                                        className="input !pr-12"
                                        data-testid="auth-password"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPw((v) => !v)}
                                        className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/80 transition-colors"
                                        tabIndex={-1}
                                        data-testid="auth-pw-eye"
                                    >
                                        {showPw ? <EyeSlash size={16} /> : <Eye size={16} />}
                                    </button>
                                </Field>

                                <AnimatePresence>
                                    {showStrength && (
                                        <motion.div
                                            initial={{ opacity: 0, height: 0 }}
                                            animate={{ opacity: 1, height: "auto" }}
                                            exit={{ opacity: 0, height: 0 }}
                                            transition={{ duration: 0.2 }}
                                            className="overflow-hidden"
                                        >
                                            <div
                                                className="mt-3 rounded-xl p-3 space-y-1.5"
                                                style={{ background: "rgba(0,0,0,0.3)", border: "1px solid rgba(255,255,255,0.06)" }}
                                                data-testid="password-strength"
                                            >
                                                <div className="text-[10px] tracking-[0.2em] uppercase mb-2" style={{ color: allPassed ? "#4ade80" : "rgba(255,255,255,0.4)" }}>
                                                    {allPassed ? t("auth.rules.looksGreat") : t("auth.rules.requirements")}
                                                </div>
                                                {PASSWORD_RULES.map((rule) => {
                                                    const passed = rule.test(password);
                                                    return (
                                                        <div key={rule.id} className="flex items-center gap-2 text-[11px]" style={{ color: passed ? "#4ade80" : "rgba(255,255,255,0.45)" }}>
                                                            {passed
                                                                ? <CheckCircle size={12} weight="fill" color="#4ade80" />
                                                                : <XCircle size={12} weight="fill" color="rgba(255,255,255,0.25)" />
                                                            }
                                                            {t(`auth.rules.${rule.id}`)}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>

                            {/* Confirm Password (signup only) */}
                            {mode === "signup" && (
                                <Field label={t("auth.confirmPassword")} icon={Lock}>
                                    <input
                                        required
                                        type={showConfirmPw ? "text" : "password"}
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        placeholder="••••••••"
                                        className="input !pr-12"
                                        data-testid="auth-confirm-password"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowConfirmPw((v) => !v)}
                                        className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/80 transition-colors"
                                        tabIndex={-1}
                                        data-testid="auth-confirm-pw-eye"
                                    >
                                        {showConfirmPw ? <EyeSlash size={16} /> : <Eye size={16} />}
                                    </button>
                                    {confirmPassword.length > 0 && (
                                        <div className={`absolute right-10 top-1/2 -translate-y-1/2 text-xs ${confirmPassword === password ? "text-green-400" : "text-red-400"}`}>
                                            {confirmPassword === password ? <CheckCircle size={14} weight="fill" /> : <XCircle size={14} weight="fill" />}
                                        </div>
                                    )}
                                </Field>
                            )}

                            {/* Forgot password link (sign in only) */}
                            {mode === "signin" && (
                                <div className="flex justify-end">
                                    <button
                                        type="button"
                                        onClick={() => { setForgotMode("email"); setForgotEmail(email); }}
                                        className="text-xs text-white/50 hover:text-[color:var(--gold)] transition-colors"
                                        data-testid="auth-forgot-link"
                                    >
                                        {t("auth.forgotPassword")}
                                    </button>
                                </div>
                            )}

                            <button type="submit" disabled={loading} className="btn-gold w-full justify-center mt-6" data-testid="auth-submit">
                                {loading ? "…" : mode === "signin" ? t("auth.signin") : t("auth.signup")}
                            </button>
                        </form>
                    </div>

                    <p className="text-center text-xs text-white/40 mt-6 leading-relaxed">
                        {t("auth.legal.agree")}{" "}
                        <Link to="/terms" className="text-white/60 hover:text-[color:var(--gold)]" data-testid="auth-terms-link">{t("auth.legal.terms")}</Link>{" "}{t("auth.legal.and")}{" "}
                        <Link to="/privacy" className="text-white/60 hover:text-[color:var(--gold)]" data-testid="auth-privacy-link">{t("auth.legal.privacy")}</Link>.
                    </p>
                </motion.div>
            </div>

            <style>{`
                .input {
                    width: 100%;
                    background: rgba(0,0,0,0.3);
                    border: 1px solid rgba(255,255,255,0.08);
                    border-radius: 12px;
                    padding: 14px 16px 14px 44px;
                    color: white;
                    font-size: 14px;
                    transition: border-color 0.3s ease;
                    color-scheme: dark;
                }
                .input::placeholder { color: rgba(255,255,255,0.35); }
                .input:focus { outline: none; border-color: var(--gold); }
                .input:-webkit-autofill,
                .input:-webkit-autofill:hover,
                .input:-webkit-autofill:focus,
                .input:-webkit-autofill:active {
                    -webkit-box-shadow: 0 0 0 1000px #080e18 inset !important;
                    -webkit-text-fill-color: #ffffff !important;
                    caret-color: #ffffff;
                    border-color: rgba(255,255,255,0.15) !important;
                }
            `}</style>
        </div>
    );
}

function Field({ label, icon: Icon, children }) {
    return (
        <label className="block">
            <span className="text-[10px] tracking-[0.2em] uppercase text-white/60 block mb-2">{label}</span>
            <div className="relative">
                <Icon size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
                {children}
            </div>
        </label>
    );
}
